import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  FiFileText, FiEdit3, FiTrash2, FiPlus, FiSave, FiLogOut, 
  FiDatabase, FiCheckCircle, FiGlobe, FiGrid, FiLayers, FiImage, 
  FiSettings, FiCode, FiSearch, FiMenu, FiX, FiCheck, FiHome, 
  FiSmartphone, FiShoppingCart, FiTrendingUp, FiMonitor, FiCpu, 
  FiPhone, FiDollarSign, FiActivity, FiUsers, FiBookOpen, FiStar, FiFolder,
  FiMail, FiWifi
} from 'react-icons/fi';
import { useSiteContent, DEFAULT_SITE_CONTENT } from '../../context/SiteContentContext';

const API_BASE = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5000';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { siteContent, blogs, siteSettings, saveSectionContent, saveBlog, deleteBlog, saveSiteSetting } = useSiteContent();

  const [activeMenu, setActiveMenu] = useState('posts'); 
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [statusMsg, setStatusMsg] = useState({ type: '', text: '' });

  // Blog Filter State
  const [blogStatusFilter, setBlogStatusFilter] = useState('all'); // 'all' | 'published' | 'draft'
  const [blogSearchQuery, setBlogSearchQuery] = useState('');

  // Categories State
  const [categoriesList, setCategoriesList] = useState([
    'Tech Insights', 'Web Development', 'UI/UX Design', 'Digital Marketing', 'E-Commerce', 'Branding & Logo'
  ]);
  const [newCatInput, setNewCatInput] = useState('');

  // Blog Form State
  const [editingBlogId, setEditingBlogId] = useState(null);
  const [blogForm, setBlogForm] = useState({
    title: '',
    category: 'Tech Insights',
    excerpt: '',
    author: 'Rojrztech',
    readTime: '5 min read',
    date: new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
    image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80&w=800',
    contentParagraphs: '',
    tagsStr: 'Technology, Rojrztech, Insights',
    status: 'published'
  });

  // Case Studies State
  const [caseStudyForm, setCaseStudyForm] = useState({
    title: '',
    client: '',
    category: 'Web App',
    image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80&w=800',
    description: ''
  });

  // Portfolio Items State (for live binding)
  const DEFAULT_PORTFOLIO_ITEMS = [
    { id: 1, title: 'Corporate Identity', category: 'Logo', img: 'https://images.unsplash.com/photo-1572044162444-ad60f128bdea?auto=format&fit=crop&q=80&w=800' },
    { id: 2, title: 'Modern UI/UX', category: 'Website Design', img: 'https://images.unsplash.com/photo-1581291518633-83b4ebd1d83e?auto=format&fit=crop&q=80&w=800' },
    { id: 3, title: 'Brand Strategy', category: 'Corporate Branding', img: 'https://images.unsplash.com/photo-1626785774573-4b799315345d?auto=format&fit=crop&q=80&w=800' },
    { id: 4, title: 'Sales Funnel', category: 'E-Commerce', img: 'https://images.unsplash.com/photo-1557821552-17105176677c?auto=format&fit=crop&q=80&w=800' },
    { id: 5, title: 'Promo Video', category: 'Video', img: 'https://images.unsplash.com/photo-1492691527719-9d1e07e534b4?auto=format&fit=crop&q=80&w=800' },
    { id: 6, title: 'Motion Graphic', category: 'Animation', img: 'https://images.unsplash.com/photo-1550745165-9bc0b252726f?auto=format&fit=crop&q=80&w=800' },
    { id: 7, title: 'Logo Design', category: 'Logo', img: 'https://images.unsplash.com/photo-1611532736597-de2d4265fba3?auto=format&fit=crop&q=80&w=800' },
    { id: 8, title: 'Web App', category: 'Website Design', img: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80&w=800' },
    { id: 9, title: 'Brand Identity', category: 'Corporate Branding', img: 'https://images.unsplash.com/photo-1547658719-da2b51169166?auto=format&fit=crop&q=80&w=800' },
    { id: 10, title: 'E-Store Design', category: 'E-Commerce', img: 'https://images.unsplash.com/photo-1563013544-824ae1b704d3?auto=format&fit=crop&q=80&w=800' },
    { id: 11, title: 'Brand Video', category: 'Video', img: 'https://images.unsplash.com/photo-1536240478700-b869ad10e097?auto=format&fit=crop&q=80&w=800' },
    { id: 12, title: '2D Animation', category: 'Animation', img: 'https://images.unsplash.com/photo-1611162616305-c69b3fa7fbe0?auto=format&fit=crop&q=80&w=800' },
  ];
  const [portfolioItems, setPortfolioItems] = useState(
    (siteContent?.portfolioItems && siteContent.portfolioItems.length > 0) ? siteContent.portfolioItems : DEFAULT_PORTFOLIO_ITEMS
  );

  // Hostinger SMTP Settings State
  const [smtpForm, setSmtpForm] = useState({
    smtpHost: 'smtp.hostinger.com',
    smtpPort: '465',
    smtpUser: '',
    smtpPass: '',
    receiverEmail: 'info@rojrztech.com'
  });
  const [smtpTestMsg, setSmtpTestMsg] = useState('');

  const [dbStatus, setDbStatus] = useState({ connected: false, database: 'reactrojrztech', host: 'localhost', loading: true });

  useEffect(() => {
    const checkDb = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/db-status`);
        if (res.ok) {
          const data = await res.json();
          setDbStatus({
            connected: data.connected,
            database: data.database || 'reactrojrztech',
            host: data.host || 'localhost',
            loading: false
          });
        }
      } catch (err) {
        setDbStatus((prev) => ({ ...prev, connected: false, loading: false }));
      }
    };
    checkDb();
  }, []);

  // Site Content & Settings Form State with Fail-Safe Defaults
  const [contentForm, setContentForm] = useState(siteContent || DEFAULT_SITE_CONTENT);
  const [settingsForm, setSettingsForm] = useState({
    logoText: 'RojrzTech',
    logoImageUrl: '',
    metaPixelId: '',
    googleAnalyticsId: '',
    headerScript: '',
    footerScript: ''
  });

  useEffect(() => {
    // Auth Check
    const token = localStorage.getItem('rojrz_admin_token');
    if (!token) {
      navigate('/admin/login');
    }
  }, [navigate]);

  useEffect(() => {
    if (siteContent && Object.keys(siteContent).length > 0) {
      setContentForm((prev) => ({ ...DEFAULT_SITE_CONTENT, ...prev, ...siteContent }));
    }
    if (siteSettings) {
      setSettingsForm((prev) => ({ ...prev, ...siteSettings }));
    }
  }, [siteContent, siteSettings]);

  const handleLogout = () => {
    localStorage.removeItem('rojrz_admin_token');
    localStorage.removeItem('rojrz_admin_user');
    navigate('/admin/login');
  };

  // Fail-Safe Content Field Accessor
  const getContentField = (menuKey, field, fallback = '') => {
    const defaultSec = DEFAULT_SITE_CONTENT[menuKey] || {};
    const defaultVal = defaultSec[field] !== undefined ? defaultSec[field] : fallback;

    if (!contentForm || !contentForm[menuKey]) return defaultVal;
    return contentForm[menuKey][field] !== undefined ? contentForm[menuKey][field] : defaultVal;
  };

  // Fail-Safe Content Field Updater
  const updateContentField = (menuKey, field, value) => {
    const defaultSec = DEFAULT_SITE_CONTENT[menuKey] || {};
    setContentForm((prev) => {
      const existingSec = prev[menuKey] || defaultSec;
      return {
        ...prev,
        [menuKey]: {
          ...defaultSec,
          ...existingSec,
          [field]: value
        }
      };
    });
  };

  // Add Custom Category
  const handleAddCategory = () => {
    if (newCatInput.trim() && !categoriesList.includes(newCatInput.trim())) {
      setCategoriesList([...categoriesList, newCatInput.trim()]);
      setBlogForm({ ...blogForm, category: newCatInput.trim() });
      setNewCatInput('');
      setStatusMsg({ type: 'success', text: `Category "${newCatInput.trim()}" added!` });
      setTimeout(() => setStatusMsg({ type: '', text: '' }), 3000);
    }
  };

  // ─── BLOG ACTIONS ──────────────────────────────────────────────────────────
  const handleBlogSubmit = async (e, forceStatus = null) => {
    if (e) e.preventDefault();
    const finalStatus = forceStatus || blogForm.status;
    setStatusMsg({ type: 'loading', text: `Saving post as ${finalStatus.toUpperCase()} in MySQL...` });

    const paragraphs = (blogForm.contentParagraphs || '')
      .split('\n\n')
      .filter((p) => p.trim())
      .map((p) => ({ type: 'paragraph', text: p.trim() }));

    const tags = (blogForm.tagsStr || '').split(',').map((t) => t.trim()).filter((t) => t);

    const payload = {
      ...(editingBlogId && { id: editingBlogId }),
      title: blogForm.title || 'Untitled Post',
      category: blogForm.category || 'Tech Insights',
      excerpt: blogForm.excerpt || '',
      author: blogForm.author || 'Rojrztech',
      readTime: blogForm.readTime || '5 min read',
      date: blogForm.date || new Date().toLocaleDateString('en-US'),
      image: blogForm.image || 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80&w=800',
      content: paragraphs.length > 0 ? paragraphs : [{ type: 'paragraph', text: blogForm.excerpt || '' }],
      tags: tags.length > 0 ? tags : ['Technology'],
      status: finalStatus
    };

    const res = await saveBlog(payload, !!editingBlogId);
    if (res && res.success) {
      setStatusMsg({ type: 'success', text: editingBlogId ? 'Post updated successfully!' : `Post saved as ${finalStatus.toUpperCase()}!` });
      resetBlogForm();
      setActiveMenu('posts');
      setTimeout(() => setStatusMsg({ type: '', text: '' }), 4000);
    } else {
      setStatusMsg({ type: 'error', text: (res && res.error) || 'Failed to save post.' });
    }
  };

  const handleEditBlog = (b) => {
    if (!b) return;
    setEditingBlogId(b.id);
    const contentText = Array.isArray(b.content)
      ? b.content.map((c) => c.text || '').join('\n\n')
      : (b.excerpt || '');

    const tagsText = Array.isArray(b.tags) ? b.tags.join(', ') : 'Technology';

    setBlogForm({
      title: b.title || '',
      category: b.category || 'Tech Insights',
      excerpt: b.excerpt || '',
      author: b.author || 'Rojrztech',
      readTime: b.readTime || '5 min read',
      date: b.date || '',
      image: b.image || '',
      contentParagraphs: contentText,
      tagsStr: tagsText,
      status: b.status || 'published'
    });
    setActiveMenu('add-post');
  };

  const handleDeleteBlog = async (id, title) => {
    if (window.confirm(`Are you sure you want to delete post "${title}"?`)) {
      setStatusMsg({ type: 'loading', text: `Deleting post #${id}...` });
      const res = await deleteBlog(id);
      if (res && res.success) {
        setStatusMsg({ type: 'success', text: `Post #${id} deleted from MySQL.` });
        setTimeout(() => setStatusMsg({ type: '', text: '' }), 3000);
      }
    }
  };

  const resetBlogForm = () => {
    setEditingBlogId(null);
    setBlogForm({
      title: '',
      category: 'Tech Insights',
      excerpt: '',
      author: 'Rojrztech',
      readTime: '5 min read',
      date: new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
      image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80&w=800',
      contentParagraphs: '',
      tagsStr: 'Technology, Rojrztech, Insights',
      status: 'published'
    });
  };

  // ─── SITE SETTINGS ACTIONS ──────────────────────────────────────────────────
  const handleSaveSettings = async (key) => {
    const val = settingsForm[key];
    setStatusMsg({ type: 'loading', text: `Saving setting '${key}' to MySQL...` });
    const res = await saveSiteSetting(key, val);
    if (res && res.success) {
      setStatusMsg({ type: 'success', text: `Setting '${key}' saved to database!` });
      setTimeout(() => setStatusMsg({ type: '', text: '' }), 3000);
    }
  };

  const handleSaveSection = async (sectionKey) => {
    setStatusMsg({ type: 'loading', text: `Saving section '${sectionKey}' to MySQL...` });
    const sectionData = contentForm[sectionKey] || DEFAULT_SITE_CONTENT[sectionKey] || {};
    const res = await saveSectionContent(sectionKey, sectionData);
    if (res && res.success) {
      setStatusMsg({ type: 'success', text: `Section '${sectionKey}' saved to database!` });
      setTimeout(() => setStatusMsg({ type: '', text: '' }), 3000);
    }
  };

  // Save Portfolio Items to MySQL (live site updates immediately)
  const handleSavePortfolioItems = async () => {
    setStatusMsg({ type: 'loading', text: 'Saving portfolio images to MySQL...' });
    const res = await saveSectionContent('portfolioItems', portfolioItems);
    if (res && res.success) {
      setStatusMsg({ type: 'success', text: 'Portfolio images saved! Live site updated.' });
      setTimeout(() => setStatusMsg({ type: '', text: '' }), 4000);
    }
  };

  // Save Hostinger SMTP Settings to MySQL
  const handleSaveSmtp = async () => {
    setStatusMsg({ type: 'loading', text: 'Saving Hostinger SMTP settings to MySQL...' });
    let allOk = true;
    for (const [key, value] of Object.entries(smtpForm)) {
      const res = await saveSiteSetting(key, value);
      if (!res || !res.success) allOk = false;
    }
    if (allOk) {
      setStatusMsg({ type: 'success', text: '✅ Hostinger SMTP settings saved! Contact form will now send real emails.' });
      setTimeout(() => setStatusMsg({ type: '', text: '' }), 5000);
    } else {
      setStatusMsg({ type: 'error', text: 'Some settings failed to save. Check MySQL connection.' });
    }
  };

  // Safe Blogs Array Filtering
  const safeBlogs = Array.isArray(blogs) ? blogs : [];
  const filteredBlogs = safeBlogs.filter((b) => {
    if (!b) return false;
    const status = b.status || 'published';
    const matchesStatus = blogStatusFilter === 'all' || status === blogStatusFilter;
    const title = (b.title || '').toLowerCase();
    const category = (b.category || '').toLowerCase();
    const query = (blogSearchQuery || '').toLowerCase();
    return matchesStatus && (title.includes(query) || category.includes(query));
  });

  const publishedCount = safeBlogs.filter((b) => (b && b.status === 'published') || (b && !b.status)).length;
  const draftCount = safeBlogs.filter((b) => b && b.status === 'draft').length;

  // Shared Inline Styles for High Contrast Cards & Inputs
  const cardStyle = {
    backgroundColor: '#18181b',
    border: '2px solid #3f3f46',
    borderRadius: '1.25rem',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.7)',
    color: '#ffffff'
  };

  const inputStyle = {
    backgroundColor: '#09090b',
    border: '2px solid #52525b',
    color: '#ffffff',
    borderRadius: '0.75rem',
    outline: 'none'
  };

  return (
    <div className="min-h-screen text-white font-sans flex flex-col md:flex-row select-none relative" style={{ backgroundColor: 'transparent' }}>
      
      {/* ─────────────────────────────────────────────────────────────────── */}
      {/* 👈 HIGH CONTRAST LEFT SIDEBAR NAVIGATION DRAWER                      */}
      {/* ─────────────────────────────────────────────────────────────────── */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-72 flex flex-col justify-between transition-transform duration-300 md:translate-x-0 ${mobileSidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}`} style={{ backgroundColor: '#121215', borderRight: '2px solid #27272a' }}>
        <div className="overflow-y-auto max-h-[calc(100vh-80px)] scrollbar-hide">
          
          {/* Brand Logo Header */}
          <div className="p-5 flex items-center justify-between sticky top-0 z-10" style={{ backgroundColor: '#121215', borderBottom: '2px solid #27272a' }}>
            <div className="flex items-center gap-3">
              <img
                src="/logo.png"
                alt="RojrzTech Logo"
                className="h-9 w-auto object-contain"
                style={{
                  filter: 'invert(1) hue-rotate(180deg) brightness(2)',
                  mixBlendMode: 'screen',
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                  if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                }}
              />
              <div className="hidden items-center gap-2">
                <div className="w-9 h-9 rounded-xl bg-[#F2B135] flex items-center justify-center font-black text-black text-xl shadow-[0_0_15px_rgba(242,177,53,0.4)]">
                  R
                </div>
                <div>
                  <h1 className="text-base font-black text-white font-display leading-tight">
                    Rojrz<span className="text-[#F2B135]">Tech</span>
                  </h1>
                  <span className="text-[9px] text-[#F2B135] font-black uppercase tracking-widest bg-[#F2B135]/15 px-2 py-0.5 rounded border border-[#F2B135]/30">
                    Master CMS
                  </span>
                </div>
              </div>
            </div>
            <button onClick={() => setMobileSidebarOpen(false)} className="md:hidden text-zinc-400">
              <FiX size={20} />
            </button>
          </div>

          {/* Navigation Links Group */}
          <nav className="p-3 space-y-6">
            
            {/* 📊 MAIN DASHBOARD */}
            <div>
              <span className="px-3 text-[10px] font-black text-[#F2B135] uppercase tracking-widest block mb-2">
                Main
              </span>
              <button
                onClick={() => { setActiveMenu('overview'); setMobileSidebarOpen(false); }}
                className={`w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-xs font-extrabold transition-all ${
                  activeMenu === 'overview'
                    ? 'bg-[#F2B135] text-black shadow-[0_0_20px_rgba(242,177,53,0.4)] font-black'
                    : 'text-zinc-200 hover:bg-zinc-900 hover:text-white border border-transparent hover:border-zinc-800'
                }`}
              >
                <FiGrid size={16} /> Dashboard Overview
              </button>
            </div>

            {/* 📝 POSTS & ARTICLES */}
            <div>
              <span className="px-3 text-[10px] font-black text-[#F2B135] uppercase tracking-widest block mb-2">
                Posts & Articles
              </span>
              <div className="space-y-1">
                <button
                  onClick={() => { setActiveMenu('posts'); setMobileSidebarOpen(false); }}
                  className={`w-full flex items-center justify-between px-3.5 py-2.5 rounded-xl text-xs font-extrabold transition-all ${
                    activeMenu === 'posts'
                      ? 'bg-[#F2B135] text-black shadow-[0_0_20px_rgba(242,177,53,0.4)] font-black'
                      : 'text-zinc-200 hover:bg-zinc-900 hover:text-white border border-transparent hover:border-zinc-800'
                  }`}
                >
                  <span className="flex items-center gap-3"><FiFileText size={16} /> All Posts</span>
                  <span className="bg-zinc-800 text-white border border-zinc-700 text-[10px] px-2 py-0.5 rounded-full font-bold">{safeBlogs.length}</span>
                </button>

                <button
                  onClick={() => { resetBlogForm(); setActiveMenu('add-post'); setMobileSidebarOpen(false); }}
                  className={`w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-xs font-extrabold transition-all ${
                    activeMenu === 'add-post'
                      ? 'bg-[#F2B135] text-black shadow-[0_0_20px_rgba(242,177,53,0.4)] font-black'
                      : 'text-zinc-200 hover:bg-zinc-900 hover:text-white border border-transparent hover:border-zinc-800'
                  }`}
                >
                  <FiPlus size={16} /> Add New Post
                </button>
              </div>
            </div>

            {/* 🖼️ CASE STUDIES & PORTFOLIO */}
            <div>
              <span className="px-3 text-[10px] font-black text-[#F2B135] uppercase tracking-widest block mb-2">
                Case Studies & Portfolio
              </span>
              <button
                onClick={() => { setActiveMenu('case-studies'); setMobileSidebarOpen(false); }}
                className={`w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-xs font-extrabold transition-all ${
                  activeMenu === 'case-studies'
                    ? 'bg-[#F2B135] text-black shadow-[0_0_20px_rgba(242,177,53,0.4)] font-black'
                    : 'text-zinc-200 hover:bg-zinc-900 hover:text-white border border-transparent hover:border-zinc-800'
                }`}
              >
                <FiFolder size={16} /> Case Studies Images
              </button>
            </div>

            {/* 🏠 CORE PAGES (HOME & ABOUT) */}
            <div>
              <span className="px-3 text-[10px] font-black text-[#F2B135] uppercase tracking-widest block mb-2">
                Core Website Pages
              </span>
              <div className="space-y-1">
                <button
                  onClick={() => { setActiveMenu('page-home'); setMobileSidebarOpen(false); }}
                  className={`w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-xs font-extrabold transition-all ${
                    activeMenu === 'page-home'
                      ? 'bg-[#F2B135] text-black shadow-[0_0_20px_rgba(242,177,53,0.4)] font-black'
                      : 'text-zinc-200 hover:bg-zinc-900 hover:text-white border border-transparent hover:border-zinc-800'
                  }`}
                >
                  <FiHome size={16} /> Home Page Content
                </button>

                <button
                  onClick={() => { setActiveMenu('page-about'); setMobileSidebarOpen(false); }}
                  className={`w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-xs font-extrabold transition-all ${
                    activeMenu === 'page-about'
                      ? 'bg-[#F2B135] text-black shadow-[0_0_20px_rgba(242,177,53,0.4)] font-black'
                      : 'text-zinc-200 hover:bg-zinc-900 hover:text-white border border-transparent hover:border-zinc-800'
                  }`}
                >
                  <FiUsers size={16} /> About Us Page
                </button>
              </div>
            </div>

            {/* 📱 DEDICATED SERVICE PAGES */}
            <div>
              <span className="px-3 text-[10px] font-black text-[#F2B135] uppercase tracking-widest block mb-2">
                Service Pages (6 Pages)
              </span>
              <div className="space-y-1">
                {[
                  { id: 'svc-web-mobile', label: 'Web & Mobile Apps', icon: <FiSmartphone size={15} /> },
                  { id: 'svc-cms-ecom', label: 'Shopify & E-Commerce', icon: <FiShoppingCart size={15} /> },
                  { id: 'svc-digital-mkt', label: 'Digital Marketing', icon: <FiTrendingUp size={15} /> },
                  { id: 'svc-graphic-video', label: 'Graphic & Video', icon: <FiGrid size={15} /> },
                  { id: 'svc-ui-ux', label: 'UI/UX Design', icon: <FiMonitor size={15} /> },
                  { id: 'svc-ai-tech', label: 'AI Technologies', icon: <FiCpu size={15} /> },
                ].map((s) => (
                  <button
                    key={s.id}
                    onClick={() => { setActiveMenu(s.id); setMobileSidebarOpen(false); }}
                    className={`w-full flex items-center gap-3 px-3.5 py-2 rounded-xl text-[11px] font-extrabold transition-all ${
                      activeMenu === s.id
                        ? 'bg-[#F2B135] text-black font-black shadow-[0_0_15px_rgba(242,177,53,0.3)]'
                        : 'text-zinc-200 hover:bg-zinc-900 hover:text-white border border-transparent hover:border-zinc-800'
                    }`}
                  >
                    {s.icon} {s.label}
                  </button>
                ))}
              </div>
            </div>

            {/* 🏥 DEDICATED INDUSTRY PAGES */}
            <div>
              <span className="px-3 text-[10px] font-black text-[#F2B135] uppercase tracking-widest block mb-2">
                Industry Pages (8 Pages)
              </span>
              <div className="space-y-1">
                {[
                  { id: 'ind-healthcare', label: 'Healthcare & Medical', icon: <FiActivity size={15} /> },
                  { id: 'ind-fitness', label: 'Fitness & Gym Apps', icon: <FiStar size={15} /> },
                  { id: 'ind-home-svc', label: 'Home Services', icon: <FiHome size={15} /> },
                  { id: 'ind-real-estate', label: 'Real Estate & Properties', icon: <FiHome size={15} /> },
                  { id: 'ind-ecommerce', label: 'Retail & E-Commerce', icon: <FiShoppingCart size={15} /> },
                  { id: 'ind-restaurant', label: 'Restaurant & Food', icon: <FiGrid size={15} /> },
                  { id: 'ind-coaching', label: 'Coaching & Courses', icon: <FiUsers size={15} /> },
                  { id: 'ind-education', label: 'EdTech & Learning', icon: <FiBookOpen size={15} /> },
                ].map((ind) => (
                  <button
                    key={ind.id}
                    onClick={() => { setActiveMenu(ind.id); setMobileSidebarOpen(false); }}
                    className={`w-full flex items-center gap-3 px-3.5 py-2 rounded-xl text-[11px] font-extrabold transition-all ${
                      activeMenu === ind.id
                        ? 'bg-[#F2B135] text-black font-black shadow-[0_0_15px_rgba(242,177,53,0.3)]'
                        : 'text-zinc-200 hover:bg-zinc-900 hover:text-white border border-transparent hover:border-zinc-800'
                    }`}
                  >
                    {ind.icon} {ind.label}
                  </button>
                ))}
              </div>
            </div>

            {/* 💰 PRICING & BRANDING */}
            <div>
              <span className="px-3 text-[10px] font-black text-[#F2B135] uppercase tracking-widest block mb-2">
                Pricing, Logo & Meta
              </span>
              <div className="space-y-1">
                <button
                  onClick={() => { setActiveMenu('pricing'); setMobileSidebarOpen(false); }}
                  className={`w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-xs font-extrabold transition-all ${
                    activeMenu === 'pricing'
                      ? 'bg-[#F2B135] text-black shadow-[0_0_20px_rgba(242,177,53,0.4)] font-black'
                      : 'text-zinc-200 hover:bg-zinc-900 hover:text-white border border-transparent hover:border-zinc-800'
                  }`}
                >
                  <FiDollarSign size={16} /> Pricing Plans Manager
                </button>

                <button
                  onClick={() => { setActiveMenu('header-logo'); setMobileSidebarOpen(false); }}
                  className={`w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-xs font-extrabold transition-all ${
                    activeMenu === 'header-logo'
                      ? 'bg-[#F2B135] text-black shadow-[0_0_20px_rgba(242,177,53,0.4)] font-black'
                      : 'text-zinc-200 hover:bg-zinc-900 hover:text-white border border-transparent hover:border-zinc-800'
                  }`}
                >
                  <FiImage size={16} /> Logo, Header & Footer
                </button>

                <button
                  onClick={() => { setActiveMenu('meta-pixel'); setMobileSidebarOpen(false); }}
                  className={`w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-xs font-extrabold transition-all ${
                    activeMenu === 'meta-pixel'
                      ? 'bg-[#F2B135] text-black shadow-[0_0_20px_rgba(242,177,53,0.4)] font-black'
                      : 'text-zinc-200 hover:bg-zinc-900 hover:text-white border border-transparent hover:border-zinc-800'
                  }`}
                >
                  <FiCode size={16} /> Meta Pixel & Analytics
                </button>

                <button
                  onClick={() => { setActiveMenu('smtp-settings'); setMobileSidebarOpen(false); }}
                  className={`w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-xs font-extrabold transition-all ${
                    activeMenu === 'smtp-settings'
                      ? 'bg-[#F2B135] text-black shadow-[0_0_20px_rgba(242,177,53,0.4)] font-black'
                      : 'text-zinc-200 hover:bg-zinc-900 hover:text-white border border-transparent hover:border-zinc-800'
                  }`}
                >
                  <FiMail size={16} /> Hostinger Email SMTP
                </button>
              </div>
            </div>

            {/* 🖼️ PORTFOLIO IMAGES MANAGER */}
            <div>
              <span className="px-3 text-[10px] font-black text-[#F2B135] uppercase tracking-widest block mb-2">
                Portfolio Images
              </span>
              <div className="space-y-1">
                <button
                  onClick={() => { setActiveMenu('portfolio-images'); setMobileSidebarOpen(false); }}
                  className={`w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-xs font-extrabold transition-all ${
                    activeMenu === 'portfolio-images'
                      ? 'bg-[#F2B135] text-black shadow-[0_0_20px_rgba(242,177,53,0.4)] font-black'
                      : 'text-zinc-200 hover:bg-zinc-900 hover:text-white border border-transparent hover:border-zinc-800'
                  }`}
                >
                  <FiImage size={16} /> Portfolio Image Editor
                </button>
              </div>
            </div>

          </nav>
        </div>

        {/* Database Status Footer */}
        <div className="p-4 border-t-2 border-zinc-800" style={{ backgroundColor: '#121215' }}>
          <div className="p-3 rounded-xl bg-zinc-900 border border-zinc-700 text-[11px] text-zinc-300">
            <div className="flex items-center gap-2 text-white font-extrabold mb-1">
              <FiDatabase className="text-[#F2B135]" /> MySQL Database Connected
            </div>
            <p className="font-mono text-[10px] text-zinc-400">reactrojrztech @ localhost</p>
          </div>
        </div>
      </aside>

      {/* ─────────────────────────────────────────────────────────────────── */}
      {/* 👉 MAIN CONTENT AREA                                                */}
      {/* ─────────────────────────────────────────────────────────────────── */}
      <main className="flex-1 md:ml-72 min-h-screen flex flex-col" style={{ backgroundColor: 'transparent' }}>
        
        {/* TOP DASHBOARD HEADER BAR */}
        <header className="px-6 py-4 flex items-center justify-between sticky top-0 z-40 shadow-2xl" style={{ backgroundColor: '#121215', borderBottom: '2px solid #27272a' }}>
          <div className="flex items-center gap-4">
            <button onClick={() => setMobileSidebarOpen(true)} className="md:hidden text-white p-2 bg-zinc-900 rounded-lg">
              <FiMenu size={22} />
            </button>

            {/* Official Logo Header */}
            <div className="flex items-center gap-3">
              <img
                src="/logo.png"
                alt="RojrzTech Logo"
                className="h-8 w-auto object-contain hidden sm:block"
                style={{
                  filter: 'invert(1) hue-rotate(180deg) brightness(2)',
                  mixBlendMode: 'screen',
                }}
              />
              <h2 className="text-base sm:text-lg font-black text-white font-display uppercase tracking-wide flex items-center gap-2">
                <span className="text-[#F2B135]">RojrzTech CMS</span>
                <span className="text-zinc-500">/</span>
                <span className="text-white">{activeMenu.toUpperCase()}</span>
              </h2>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Link
              to="/"
              target="_blank"
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-zinc-900 border border-zinc-700 text-white text-xs font-bold hover:text-[#F2B135] hover:border-[#F2B135] transition-all shadow-md"
            >
              <FiGlobe size={14} /> Live Site ↗
            </Link>

            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-red-500/20 border border-red-500/40 text-red-400 text-xs font-bold hover:bg-red-500 hover:text-white transition-all shadow-md"
            >
              <FiLogOut size={14} /> Logout
            </button>
          </div>
        </header>

        {/* STATUS NOTIFICATION BAR */}
        {statusMsg.text && (
          <div className="bg-[#F2B135] text-black px-6 py-3 text-center text-xs font-black flex items-center justify-center gap-2 shadow-lg">
            <span className="w-2.5 h-2.5 rounded-full bg-black animate-ping" />
            {statusMsg.text}
          </div>
        )}

        {/* ── MAIN BODY CONTENT CONTAINER ── */}
        <div className="p-6 sm:p-8 flex-1 max-w-7xl">
          
          {/* ================================================================= */}
          {/* 📝 VIEW 1: ALL POSTS MANAGER TABLE                                */}
          {/* ================================================================= */}
          {activeMenu === 'posts' && (
            <div className="space-y-6">
              
              {/* TOP ACTION BAR */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4 p-5 rounded-2xl shadow-2xl" style={cardStyle}>
                
                {/* Status Tabs */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setBlogStatusFilter('all')}
                    className={`px-4 py-2 rounded-xl text-xs font-black transition-all ${
                      blogStatusFilter === 'all' ? 'bg-[#F2B135] text-black shadow-md' : 'bg-zinc-900 border border-zinc-700 text-white hover:text-[#F2B135]'
                    }`}
                  >
                    All Posts ({safeBlogs.length})
                  </button>
                  <button
                    onClick={() => setBlogStatusFilter('published')}
                    className={`px-4 py-2 rounded-xl text-xs font-black transition-all ${
                      blogStatusFilter === 'published' ? 'bg-green-500 text-black shadow-md' : 'bg-zinc-900 border border-zinc-700 text-white hover:text-green-400'
                    }`}
                  >
                    Published ({publishedCount})
                  </button>
                  <button
                    onClick={() => setBlogStatusFilter('draft')}
                    className={`px-4 py-2 rounded-xl text-xs font-black transition-all ${
                      blogStatusFilter === 'draft' ? 'bg-amber-500 text-black shadow-md' : 'bg-zinc-900 border border-zinc-700 text-white hover:text-amber-400'
                    }`}
                  >
                    Drafts ({draftCount})
                  </button>
                </div>

                {/* Search & Add New Button */}
                <div className="flex items-center gap-3">
                  <div className="relative flex-1 sm:w-64">
                    <input
                      type="text"
                      value={blogSearchQuery}
                      onChange={(e) => setBlogSearchQuery(e.target.value)}
                      placeholder="Search posts..."
                      style={inputStyle}
                      className="w-full pl-9 pr-4 py-2.5 text-xs font-bold"
                    />
                    <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" size={14} />
                  </div>

                  <button
                    onClick={() => { resetBlogForm(); setActiveMenu('add-post'); }}
                    className="btn-animated-gold !py-2.5 !px-5 text-xs font-black flex items-center gap-1.5 shrink-0"
                  >
                    <FiPlus size={16} /> Add New Post
                  </button>
                </div>

              </div>

              {/* POSTS TABLE */}
              <div className="rounded-3xl overflow-hidden shadow-2xl" style={cardStyle}>
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs border-collapse">
                    <thead>
                      <tr className="bg-zinc-950 text-white font-black uppercase tracking-wider border-b-2 border-zinc-800">
                        <th className="py-4 px-6">Post Title</th>
                        <th className="py-4 px-4">Author</th>
                        <th className="py-4 px-4">Category</th>
                        <th className="py-4 px-4">Status</th>
                        <th className="py-4 px-4">Date</th>
                        <th className="py-4 px-6 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-800">
                      {filteredBlogs.length === 0 ? (
                        <tr>
                          <td colSpan="6" className="py-12 text-center text-zinc-400 font-bold">
                            No posts found. Click "+ Add New Post" to create one.
                          </td>
                        </tr>
                      ) : (
                        filteredBlogs.map((b) => (
                          <tr key={b.id || b.title} className="hover:bg-zinc-900/80 transition-colors group">
                            <td className="py-4 px-6">
                              <div className="flex items-center gap-3">
                                <img
                                  src={b.image || 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80&w=800'}
                                  alt={b.title}
                                  className="w-12 h-12 rounded-xl object-cover border border-zinc-700 shrink-0"
                                />
                                <div>
                                  <h4 className="font-extrabold text-white group-hover:text-[#F2B135] transition-colors max-w-xs truncate text-sm">
                                    {b.title}
                                  </h4>
                                  <p className="text-[11px] text-zinc-400 truncate max-w-xs mt-0.5">
                                    {b.excerpt}
                                  </p>
                                </div>
                              </div>
                            </td>

                            <td className="py-4 px-4 text-white font-bold">{b.author || 'Rojrztech'}</td>

                            <td className="py-4 px-4">
                              <span className="bg-zinc-950 border border-zinc-700 text-[#F2B135] text-[10px] font-black px-2.5 py-1 rounded-lg">
                                {b.category || 'Tech Insights'}
                              </span>
                            </td>

                            <td className="py-4 px-4">
                              {b.status === 'draft' ? (
                                <span className="bg-amber-500/20 border border-amber-500/50 text-amber-300 text-[10px] font-black px-2.5 py-1 rounded-lg uppercase tracking-wider">
                                  Draft
                                </span>
                              ) : (
                                <span className="bg-green-500/20 border border-green-500/50 text-green-300 text-[10px] font-black px-2.5 py-1 rounded-lg uppercase tracking-wider">
                                  Published
                                </span>
                              )}
                            </td>

                            <td className="py-4 px-4 text-zinc-300 text-xs font-mono font-medium">{b.date || 'Recent'}</td>

                            <td className="py-4 px-6 text-right">
                              <div className="flex items-center justify-end gap-2">
                                <button
                                  onClick={() => handleEditBlog(b)}
                                  title="Edit Post"
                                  className="p-2.5 rounded-xl bg-zinc-900 border border-zinc-700 text-white hover:border-[#F2B135] hover:text-[#F2B135] transition-all"
                                >
                                  <FiEdit3 size={15} />
                                </button>

                                <button
                                  onClick={() => handleDeleteBlog(b.id, b.title)}
                                  title="Delete Post"
                                  className="p-2.5 rounded-xl bg-red-500/20 border border-red-500/40 text-red-400 hover:bg-red-500 hover:text-white transition-all"
                                >
                                  <FiTrash2 size={15} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

            </div>
          )}

          {/* ================================================================= */}
          {/* ➕ VIEW 2: ADD / EDIT BLOG POST FORM                              */}
          {/* ================================================================= */}
          {activeMenu === 'add-post' && (
            <div className="p-6 sm:p-8 max-w-4xl shadow-2xl space-y-6" style={cardStyle}>
              <div className="flex items-center justify-between pb-4 border-b-2 border-zinc-800">
                <h3 className="text-lg font-black text-[#F2B135] uppercase tracking-wider flex items-center gap-2">
                  <FiEdit3 size={20} /> {editingBlogId ? `Editing Post #${editingBlogId}` : 'Publish New Blog Post'}
                </h3>
                <button
                  onClick={() => setActiveMenu('posts')}
                  className="px-4 py-2 rounded-xl bg-zinc-900 border border-zinc-700 text-white hover:text-[#F2B135] text-xs font-black"
                >
                  ← Back to Posts
                </button>
              </div>

              <form onSubmit={(e) => handleBlogSubmit(e, blogForm.status)} className="space-y-6">
                
                {/* POST TITLE */}
                <div>
                  <label className="text-xs font-black text-zinc-200 uppercase tracking-wider block mb-2">
                    Post Title *
                  </label>
                  <input
                    type="text"
                    required
                    value={blogForm.title}
                    onChange={(e) => setBlogForm({ ...blogForm, title: e.target.value })}
                    placeholder="Enter blog post title..."
                    style={inputStyle}
                    className="w-full px-4 py-3.5 text-sm font-bold shadow-inner"
                  />
                </div>

                {/* CATEGORY, STATUS, READ TIME */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="text-xs font-black text-zinc-200 uppercase tracking-wider block mb-2">
                      Select Category
                    </label>
                    <select
                      value={blogForm.category}
                      onChange={(e) => setBlogForm({ ...blogForm, category: e.target.value })}
                      style={inputStyle}
                      className="w-full px-4 py-3.5 text-xs font-bold"
                    >
                      {categoriesList.map((cat) => (
                        <option key={cat} value={cat} style={{ backgroundColor: '#18181b', color: '#fff' }}>{cat}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="text-xs font-black text-zinc-200 uppercase tracking-wider block mb-2">
                      Post Status
                    </label>
                    <select
                      value={blogForm.status}
                      onChange={(e) => setBlogForm({ ...blogForm, status: e.target.value })}
                      style={inputStyle}
                      className="w-full px-4 py-3.5 text-xs font-bold"
                    >
                      <option value="published" style={{ backgroundColor: '#18181b', color: '#fff' }}>🟢 Published (Live)</option>
                      <option value="draft" style={{ backgroundColor: '#18181b', color: '#fff' }}>🟡 Save as Draft</option>
                    </select>
                  </div>

                  <div>
                    <label className="text-xs font-black text-zinc-200 uppercase tracking-wider block mb-2">
                      Estimated Read Time
                    </label>
                    <input
                      type="text"
                      value={blogForm.readTime}
                      onChange={(e) => setBlogForm({ ...blogForm, readTime: e.target.value })}
                      placeholder="e.g. 5 min read"
                      style={inputStyle}
                      className="w-full px-4 py-3.5 text-xs font-bold"
                    />
                  </div>
                </div>

                {/* ADD NEW CATEGORY TOOL */}
                <div className="p-4 rounded-2xl bg-zinc-950 border-2 border-zinc-800 flex flex-col sm:flex-row items-center gap-3">
                  <span className="text-xs font-black text-[#F2B135] whitespace-nowrap">➕ Add New Category:</span>
                  <input
                    type="text"
                    value={newCatInput}
                    onChange={(e) => setNewCatInput(e.target.value)}
                    placeholder="Enter new category name..."
                    style={inputStyle}
                    className="flex-1 w-full px-3 py-2 text-xs font-bold"
                  />
                  <button
                    type="button"
                    onClick={handleAddCategory}
                    className="px-4 py-2 rounded-xl bg-[#F2B135] text-black font-black text-xs shrink-0 hover:bg-[#d4941e]"
                  >
                    Add Category
                  </button>
                </div>

                {/* FEATURED IMAGE URL */}
                <div>
                  <label className="text-xs font-black text-zinc-200 uppercase tracking-wider block mb-2">
                    Featured Image URL *
                  </label>
                  <input
                    type="text"
                    required
                    value={blogForm.image}
                    onChange={(e) => setBlogForm({ ...blogForm, image: e.target.value })}
                    placeholder="https://images.unsplash.com/photo-..."
                    style={inputStyle}
                    className="w-full px-4 py-3.5 text-xs font-bold"
                  />
                </div>

                {/* EXCERPT */}
                <div>
                  <label className="text-xs font-black text-zinc-200 uppercase tracking-wider block mb-2">
                    Excerpt (Short Summary for Cards) *
                  </label>
                  <textarea
                    rows="2"
                    required
                    value={blogForm.excerpt}
                    onChange={(e) => setBlogForm({ ...blogForm, excerpt: e.target.value })}
                    placeholder="Enter short summary..."
                    style={inputStyle}
                    className="w-full px-4 py-3.5 text-xs font-bold resize-none"
                  />
                </div>

                {/* ARTICLE CONTENT */}
                <div>
                  <label className="text-xs font-black text-zinc-200 uppercase tracking-wider block mb-2">
                    Article Main Content (Separate paragraphs with double Enter) *
                  </label>
                  <textarea
                    rows="8"
                    required
                    value={blogForm.contentParagraphs}
                    onChange={(e) => setBlogForm({ ...blogForm, contentParagraphs: e.target.value })}
                    placeholder="Write detailed post content here..."
                    style={inputStyle}
                    className="w-full px-4 py-3.5 text-xs font-medium resize-none"
                  />
                </div>

                {/* ACTION BUTTONS */}
                <div className="flex flex-col sm:flex-row items-center gap-4 pt-4 border-t-2 border-zinc-800">
                  <button
                    type="button"
                    onClick={(e) => handleBlogSubmit(e, 'published')}
                    className="btn-animated-gold w-full sm:flex-1 py-4 text-xs font-black uppercase tracking-widest flex items-center justify-center gap-2 shadow-xl"
                  >
                    <FiSave size={18} /> Publish Post to Live Site
                  </button>

                  <button
                    type="button"
                    onClick={(e) => handleBlogSubmit(e, 'draft')}
                    className="w-full sm:w-auto px-8 py-4 rounded-xl bg-zinc-900 border-2 border-zinc-700 text-white hover:border-amber-400 hover:text-amber-400 font-black text-xs uppercase tracking-widest transition-all"
                  >
                    Save as Draft
                  </button>
                </div>

              </form>
            </div>
          )}

          {/* ================================================================= */}
          {/* 🖼️ VIEW 3: CASE STUDIES & PORTFOLIO IMAGES MANAGER                */}
          {/* ================================================================= */}
          {activeMenu === 'case-studies' && (
            <div className="space-y-6 max-w-4xl">
              <div className="p-6 sm:p-8 shadow-2xl space-y-6" style={cardStyle}>
                <div className="flex items-center justify-between pb-4 border-b-2 border-zinc-800">
                  <h3 className="text-base font-black text-[#F2B135] uppercase tracking-wider flex items-center gap-2">
                    <FiFolder size={20} /> Case Studies & Portfolio Images Manager
                  </h3>
                  <button
                    onClick={() => handleSaveSection('caseStudies')}
                    className="btn-animated-gold !py-2 !px-4 text-xs"
                  >
                    <FiSave size={14} /> Save Case Studies
                  </button>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="text-xs font-black text-zinc-200 uppercase tracking-wider block mb-2">Project Title</label>
                    <input
                      type="text"
                      value={caseStudyForm.title}
                      onChange={(e) => setCaseStudyForm({ ...caseStudyForm, title: e.target.value })}
                      placeholder="e.g. Next-Gen FinTech Mobile App"
                      style={inputStyle}
                      className="w-full px-4 py-3 text-xs font-bold"
                    />
                  </div>

                  <div>
                    <label className="text-xs font-black text-zinc-200 uppercase tracking-wider block mb-2">Case Study Image URL</label>
                    <input
                      type="text"
                      value={caseStudyForm.image}
                      onChange={(e) => setCaseStudyForm({ ...caseStudyForm, image: e.target.value })}
                      placeholder="https://images.unsplash.com/..."
                      style={inputStyle}
                      className="w-full px-4 py-3 text-xs font-bold"
                    />
                  </div>

                  <div>
                    <label className="text-xs font-black text-zinc-200 uppercase tracking-wider block mb-2">Project Description</label>
                    <textarea
                      rows="3"
                      value={caseStudyForm.description}
                      onChange={(e) => setCaseStudyForm({ ...caseStudyForm, description: e.target.value })}
                      placeholder="Project details..."
                      style={inputStyle}
                      className="w-full px-4 py-3 text-xs font-medium resize-none"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ================================================================= */}
          {/* 🏠 VIEW 4: HOME PAGE CONTENT CMS                                  */}
          {/* ================================================================= */}
          {activeMenu === 'page-home' && (
            <div className="space-y-6 max-w-4xl">
              <div className="p-6 sm:p-8 shadow-2xl space-y-6" style={cardStyle}>
                <div className="flex items-center justify-between pb-4 border-b-2 border-zinc-800">
                  <h3 className="text-base font-black text-[#F2B135] uppercase tracking-wider flex items-center gap-2">
                    <FiHome size={20} /> Home Page Hero & Main Headings
                  </h3>
                  <button
                    onClick={() => handleSaveSection('hero')}
                    className="btn-animated-gold !py-2 !px-4 text-xs"
                  >
                    <FiSave size={14} /> Save Hero Section
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-black text-zinc-200 uppercase tracking-wider block mb-2">Badge Tagline</label>
                    <input
                      type="text"
                      value={getContentField('hero', 'badge', '#1 DIGITAL AGENCY IN TECH')}
                      onChange={(e) => updateContentField('hero', 'badge', e.target.value)}
                      style={inputStyle}
                      className="w-full px-4 py-3 text-xs font-bold"
                    />
                  </div>

                  <div>
                    <label className="text-xs font-black text-zinc-200 uppercase tracking-wider block mb-2">Primary Button Label</label>
                    <input
                      type="text"
                      value={getContentField('hero', 'primaryBtnText', 'Start Your Project')}
                      onChange={(e) => updateContentField('hero', 'primaryBtnText', e.target.value)}
                      style={inputStyle}
                      className="w-full px-4 py-3 text-xs font-bold"
                    />
                  </div>

                  <div>
                    <label className="text-xs font-black text-zinc-200 uppercase tracking-wider block mb-2">Hero Title Line 1</label>
                    <input
                      type="text"
                      value={getContentField('hero', 'titleLine1', 'Transform Your')}
                      onChange={(e) => updateContentField('hero', 'titleLine1', e.target.value)}
                      style={inputStyle}
                      className="w-full px-4 py-3 text-xs font-bold"
                    />
                  </div>

                  <div>
                    <label className="text-xs font-black text-zinc-200 uppercase tracking-wider block mb-2">Hero Title Line 2 (Highlighted)</label>
                    <input
                      type="text"
                      value={getContentField('hero', 'titleLine2', 'Digital Vision')}
                      onChange={(e) => updateContentField('hero', 'titleLine2', e.target.value)}
                      style={inputStyle}
                      className="w-full px-4 py-3 text-xs font-bold"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="text-xs font-black text-zinc-200 uppercase tracking-wider block mb-2">Hero Subtitle Description</label>
                    <textarea
                      rows="3"
                      value={getContentField('hero', 'subtitle', 'RojrzTech is a leading software and digital marketing agency.')}
                      onChange={(e) => updateContentField('hero', 'subtitle', e.target.value)}
                      style={inputStyle}
                      className="w-full px-4 py-3 text-xs font-medium resize-none"
                    />
                  </div>
                </div>
              </div>

              {/* Services Header Card */}
              <div className="p-6 sm:p-8 shadow-2xl space-y-6" style={cardStyle}>
                <div className="flex items-center justify-between pb-4 border-b-2 border-zinc-800">
                  <h3 className="text-base font-black text-[#F2B135] uppercase tracking-wider flex items-center gap-2">
                    <FiLayers size={20} /> Services Section Header
                  </h3>
                  <button
                    onClick={() => handleSaveSection('servicesHeader')}
                    className="btn-animated-gold !py-2 !px-4 text-xs"
                  >
                    <FiSave size={14} /> Save Services Header
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-black text-zinc-200 uppercase tracking-wider block mb-2">Badge Tagline</label>
                    <input
                      type="text"
                      value={getContentField('servicesHeader', 'badge', 'Our Services')}
                      onChange={(e) => updateContentField('servicesHeader', 'badge', e.target.value)}
                      style={inputStyle}
                      className="w-full px-4 py-3 text-xs font-bold"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-black text-zinc-200 uppercase tracking-wider block mb-2">Title Prefix</label>
                    <input
                      type="text"
                      value={getContentField('servicesHeader', 'titlePrefix', 'RojrzTech: Your')}
                      onChange={(e) => updateContentField('servicesHeader', 'titlePrefix', e.target.value)}
                      style={inputStyle}
                      className="w-full px-4 py-3 text-xs font-bold"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-black text-zinc-200 uppercase tracking-wider block mb-2">Title Highlighted Text</label>
                    <input
                      type="text"
                      value={getContentField('servicesHeader', 'titleHighlight', 'Integrated Digital')}
                      onChange={(e) => updateContentField('servicesHeader', 'titleHighlight', e.target.value)}
                      style={inputStyle}
                      className="w-full px-4 py-3 text-xs font-bold"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-black text-zinc-200 uppercase tracking-wider block mb-2">Title Suffix</label>
                    <input
                      type="text"
                      value={getContentField('servicesHeader', 'titleSuffix', 'Solutions Partner')}
                      onChange={(e) => updateContentField('servicesHeader', 'titleSuffix', e.target.value)}
                      style={inputStyle}
                      className="w-full px-4 py-3 text-xs font-bold"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="text-xs font-black text-zinc-200 uppercase tracking-wider block mb-2">Subtitle Description</label>
                    <textarea
                      rows="2"
                      value={getContentField('servicesHeader', 'subtitle', 'At RojrzTech, we provide a full spectrum of digital services.')}
                      onChange={(e) => updateContentField('servicesHeader', 'subtitle', e.target.value)}
                      style={inputStyle}
                      className="w-full px-4 py-3 text-xs font-medium resize-none"
                    />
                  </div>
                </div>
              </div>

              {/* Case Studies Header Card */}
              <div className="p-6 sm:p-8 shadow-2xl space-y-6" style={cardStyle}>
                <div className="flex items-center justify-between pb-4 border-b-2 border-zinc-800">
                  <h3 className="text-base font-black text-[#F2B135] uppercase tracking-wider flex items-center gap-2">
                    <FiFolder size={20} /> Case Studies Section Header
                  </h3>
                  <button
                    onClick={() => handleSaveSection('caseStudies')}
                    className="btn-animated-gold !py-2 !px-4 text-xs"
                  >
                    <FiSave size={14} /> Save Case Studies Header
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="text-xs font-black text-zinc-200 uppercase tracking-wider block mb-2">Section Title</label>
                    <input
                      type="text"
                      value={getContentField('caseStudies', 'title', 'Featured Case Studies')}
                      onChange={(e) => updateContentField('caseStudies', 'title', e.target.value)}
                      style={inputStyle}
                      className="w-full px-4 py-3 text-xs font-bold"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="text-xs font-black text-zinc-200 uppercase tracking-wider block mb-2">Section Subtitle</label>
                    <textarea
                      rows="2"
                      value={getContentField('caseStudies', 'subtitle', 'Explore our latest web design, branding, and mobile projects.')}
                      onChange={(e) => updateContentField('caseStudies', 'subtitle', e.target.value)}
                      style={inputStyle}
                      className="w-full px-4 py-3 text-xs font-medium resize-none"
                    />
                  </div>
                </div>
              </div>

              {/* Contact Details Card */}
              <div className="p-6 sm:p-8 shadow-2xl space-y-6" style={cardStyle}>
                <div className="flex items-center justify-between pb-4 border-b-2 border-zinc-800">
                  <h3 className="text-base font-black text-[#F2B135] uppercase tracking-wider flex items-center gap-2">
                    <FiPhone size={20} /> Contact Details Section
                  </h3>
                  <button
                    onClick={() => handleSaveSection('contact')}
                    className="btn-animated-gold !py-2 !px-4 text-xs"
                  >
                    <FiSave size={14} /> Save Contact Details
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-black text-zinc-200 uppercase tracking-wider block mb-2">Badge Tagline</label>
                    <input
                      type="text"
                      value={getContentField('contact', 'badge', "Let's Collaborate")}
                      onChange={(e) => updateContentField('contact', 'badge', e.target.value)}
                      style={inputStyle}
                      className="w-full px-4 py-3 text-xs font-bold"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-black text-zinc-200 uppercase tracking-wider block mb-2">Main Heading</label>
                    <input
                      type="text"
                      value={getContentField('contact', 'heading', 'Ready To Build Your Next Venture?')}
                      onChange={(e) => updateContentField('contact', 'heading', e.target.value)}
                      style={inputStyle}
                      className="w-full px-4 py-3 text-xs font-bold"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-black text-zinc-200 uppercase tracking-wider block mb-2">Contact Email</label>
                    <input
                      type="email"
                      value={getContentField('contact', 'email', 'info@rojrztech.com')}
                      onChange={(e) => updateContentField('contact', 'email', e.target.value)}
                      style={inputStyle}
                      className="w-full px-4 py-3 text-xs font-bold"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-black text-zinc-200 uppercase tracking-wider block mb-2">Contact Phone</label>
                    <input
                      type="text"
                      value={getContentField('contact', 'phone', '+1 (512) 254-4193')}
                      onChange={(e) => updateContentField('contact', 'phone', e.target.value)}
                      style={inputStyle}
                      className="w-full px-4 py-3 text-xs font-bold"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="text-xs font-black text-zinc-200 uppercase tracking-wider block mb-2">Physical Office Address</label>
                    <input
                      type="text"
                      value={getContentField('contact', 'address', '5900, Balcones Drive, STE 100, Austin, Texas, 78731')}
                      onChange={(e) => updateContentField('contact', 'address', e.target.value)}
                      style={inputStyle}
                      className="w-full px-4 py-3 text-xs font-bold"
                    />
                  </div>
                </div>
              </div>

              {/* CTA Section Card */}
              <div className="p-6 sm:p-8 shadow-2xl space-y-6" style={cardStyle}>
                <div className="flex items-center justify-between pb-4 border-b-2 border-zinc-800">
                  <h3 className="text-base font-black text-[#F2B135] uppercase tracking-wider flex items-center gap-2">
                    <FiCheckCircle size={20} /> Home Page CTA Banner
                  </h3>
                  <button
                    onClick={() => handleSaveSection('cta')}
                    className="btn-animated-gold !py-2 !px-4 text-xs"
                  >
                    <FiSave size={14} /> Save CTA Banner
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-black text-zinc-200 uppercase tracking-wider block mb-2">Badge Tagline</label>
                    <input
                      type="text"
                      value={getContentField('cta', 'badge', "Let's Make Magic Happen")}
                      onChange={(e) => updateContentField('cta', 'badge', e.target.value)}
                      style={inputStyle}
                      className="w-full px-4 py-3 text-xs font-bold"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-black text-zinc-200 uppercase tracking-wider block mb-2">Primary Button Text</label>
                    <input
                      type="text"
                      value={getContentField('cta', 'primaryBtnText', 'Get Started Now')}
                      onChange={(e) => updateContentField('cta', 'primaryBtnText', e.target.value)}
                      style={inputStyle}
                      className="w-full px-4 py-3 text-xs font-bold"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-black text-zinc-200 uppercase tracking-wider block mb-2">Title Line 1</label>
                    <input
                      type="text"
                      value={getContentField('cta', 'titleLine1', 'Have a Great Idea?')}
                      onChange={(e) => updateContentField('cta', 'titleLine1', e.target.value)}
                      style={inputStyle}
                      className="w-full px-4 py-3 text-xs font-bold"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-black text-zinc-200 uppercase tracking-wider block mb-2">Title Line 2 (Action)</label>
                    <input
                      type="text"
                      value={getContentField('cta', 'titleLine2', "Let's Build It")}
                      onChange={(e) => updateContentField('cta', 'titleLine2', e.target.value)}
                      style={inputStyle}
                      className="w-full px-4 py-3 text-xs font-bold"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="text-xs font-black text-zinc-200 uppercase tracking-wider block mb-2">Title Line 3 (Highlight)</label>
                    <input
                      type="text"
                      value={getContentField('cta', 'titleLine3', 'Into Reality.')}
                      onChange={(e) => updateContentField('cta', 'titleLine3', e.target.value)}
                      style={inputStyle}
                      className="w-full px-4 py-3 text-xs font-bold"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="text-xs font-black text-zinc-200 uppercase tracking-wider block mb-2">Description Subtitle</label>
                    <textarea
                      rows="3"
                      value={getContentField('cta', 'subtitle', 'Stop waiting for the perfect time. Drop us a message today.')}
                      onChange={(e) => updateContentField('cta', 'subtitle', e.target.value)}
                      style={inputStyle}
                      className="w-full px-4 py-3 text-xs font-medium resize-none"
                    />
                  </div>
                </div>
              </div>

            </div>
          )}

          {/* ================================================================= */}
          {/* ℹ️ VIEW 5: ABOUT US PAGE CONTENT CMS                              */}
          {/* ================================================================= */}
          {activeMenu === 'page-about' && (
            <div className="space-y-6 max-w-4xl">
              <div className="p-6 sm:p-8 shadow-2xl space-y-6" style={cardStyle}>
                <div className="flex items-center justify-between pb-4 border-b-2 border-zinc-800">
                  <h3 className="text-base font-black text-[#F2B135] uppercase tracking-wider flex items-center gap-2">
                    <FiUsers size={20} /> About Us Page Content
                  </h3>
                  <button
                    onClick={() => handleSaveSection('about')}
                    className="btn-animated-gold !py-2 !px-4 text-xs"
                  >
                    <FiSave size={14} /> Save About Page
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-black text-zinc-200 uppercase tracking-wider block mb-2">Main Heading</label>
                    <input
                      type="text"
                      value={getContentField('about', 'heading', 'Empowering Your Digital Journey')}
                      onChange={(e) => updateContentField('about', 'heading', e.target.value)}
                      style={inputStyle}
                      className="w-full px-4 py-3 text-xs font-bold"
                    />
                  </div>

                  <div>
                    <label className="text-xs font-black text-zinc-200 uppercase tracking-wider block mb-2">Featured Team Image URL</label>
                    <input
                      type="text"
                      value={getContentField('about', 'image', 'https://images.unsplash.com/photo-1522071820081-009f0129c71c')}
                      onChange={(e) => updateContentField('about', 'image', e.target.value)}
                      style={inputStyle}
                      className="w-full px-4 py-3 text-xs font-bold"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="text-xs font-black text-zinc-200 uppercase tracking-wider block mb-2">About Story Paragraph</label>
                    <textarea
                      rows="4"
                      value={getContentField('about', 'desc1', 'We are a fully registered LLC software agency.')}
                      onChange={(e) => updateContentField('about', 'desc1', e.target.value)}
                      style={inputStyle}
                      className="w-full px-4 py-3 text-xs font-medium resize-none"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ================================================================= */}
          {/* 📱 VIEW 6 & 7: DEDICATED SERVICE / INDUSTRY PAGES CMS             */}
          {/* ================================================================= */}
          {(activeMenu.startsWith('svc-') || activeMenu.startsWith('ind-')) && (
            <div className="space-y-6 max-w-4xl">
              <div className="p-6 sm:p-8 shadow-2xl space-y-6" style={cardStyle}>
                <div className="flex items-center justify-between pb-4 border-b-2 border-zinc-800">
                  <h3 className="text-base font-black text-[#F2B135] uppercase tracking-wider flex items-center gap-2">
                    <FiLayers size={20} /> {activeMenu.replace('svc-', 'Service: ').replace('ind-', 'Industry: ').toUpperCase()} Editor
                  </h3>
                  <button
                    onClick={() => handleSaveSection(activeMenu)}
                    className="btn-animated-gold !py-2 !px-4 text-xs"
                  >
                    <FiSave size={14} /> Save Page Content
                  </button>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="text-xs font-black text-zinc-200 uppercase tracking-wider block mb-2">Page Main Title</label>
                    <input
                      type="text"
                      value={getContentField(activeMenu, 'title', `Custom ${activeMenu.replace('svc-', '').replace('ind-', '')} Solutions`)}
                      onChange={(e) => updateContentField(activeMenu, 'title', e.target.value)}
                      placeholder="Enter page title..."
                      style={inputStyle}
                      className="w-full px-4 py-3 text-xs font-bold"
                    />
                  </div>

                  <div>
                    <label className="text-xs font-black text-zinc-200 uppercase tracking-wider block mb-2">Page Subtitle Description</label>
                    <textarea
                      rows="4"
                      value={getContentField(activeMenu, 'subtitle', 'Tailored digital solutions engineered for maximum performance.')}
                      onChange={(e) => updateContentField(activeMenu, 'subtitle', e.target.value)}
                      placeholder="Enter page description..."
                      style={inputStyle}
                      className="w-full px-4 py-3 text-xs font-medium resize-none"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ================================================================= */}
          {/* 💰 VIEW 8: PRICING PLANS MANAGER                                  */}
          {/* ================================================================= */}
          {activeMenu === 'pricing' && (
            <div className="space-y-6 max-w-4xl">
              <div className="p-6 sm:p-8 shadow-2xl space-y-6" style={cardStyle}>
                <div className="flex items-center justify-between pb-4 border-b-2 border-zinc-800">
                  <h3 className="text-base font-black text-[#F2B135] uppercase tracking-wider flex items-center gap-2">
                    <FiDollarSign size={20} /> Pricing Packages & Rates Manager
                  </h3>
                  <button
                    onClick={() => handleSaveSection('pricing')}
                    className="btn-animated-gold !py-2 !px-4 text-xs"
                  >
                    <FiSave size={14} /> Save Pricing Plans
                  </button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="p-4 rounded-2xl" style={{ backgroundColor: '#09090b', border: '2px solid #3f3f46' }}>
                    <h4 className="text-xs font-black text-[#F2B135] uppercase mb-2">Web Design Startup Package</h4>
                    <p className="text-sm font-black text-white mb-2">Price: $150.00 USD</p>
                    <p className="text-xs text-zinc-300 font-medium">10 customizable pages, mobile responsive, basic SEO.</p>
                  </div>

                  <div className="p-4 rounded-2xl" style={{ backgroundColor: '#09090b', border: '2px solid #3f3f46' }}>
                    <h4 className="text-xs font-black text-[#F2B135] uppercase mb-2">Web Design Professional Package</h4>
                    <p className="text-sm font-black text-white mb-2">Price: $250.00 USD</p>
                    <p className="text-xs text-zinc-300 font-medium">50 pages, custom lead forms, priority support.</p>
                  </div>

                  <div className="p-4 rounded-2xl" style={{ backgroundColor: '#09090b', border: '2px solid #3f3f46' }}>
                    <h4 className="text-xs font-black text-[#F2B135] uppercase mb-2">Logo Design Package</h4>
                    <p className="text-sm font-black text-white mb-2">Price: $25.00 USD</p>
                    <p className="text-xs text-zinc-300 font-medium">3 unique logo concepts, 100% ownership rights.</p>
                  </div>

                  <div className="p-4 rounded-2xl" style={{ backgroundColor: '#09090b', border: '2px solid #3f3f46' }}>
                    <h4 className="text-xs font-black text-[#F2B135] uppercase mb-2">SEO Optimization Package</h4>
                    <p className="text-sm font-black text-white mb-2">Price: $150.00 USD</p>
                    <p className="text-xs text-zinc-300 font-medium">Technical SEO audit, Google rank #1 strategy.</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ================================================================= */}
          {/* 🎨 VIEW 9: HEADER, LOGO & FOOTER CMS                              */}
          {/* ================================================================= */}
          {activeMenu === 'header-logo' && (
            <div className="space-y-6 max-w-4xl">
              <div className="p-6 sm:p-8 shadow-2xl space-y-6" style={cardStyle}>
                <div className="flex items-center justify-between pb-4 border-b-2 border-zinc-800">
                  <h3 className="text-base font-black text-[#F2B135] uppercase tracking-wider flex items-center gap-2">
                    <FiImage size={20} /> Site Logo Settings
                  </h3>
                  <button
                    onClick={() => { handleSaveSettings('logoText'); handleSaveSettings('logoImageUrl'); }}
                    className="btn-animated-gold !py-2 !px-4 text-xs"
                  >
                    <FiSave size={14} /> Save Logo
                  </button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-black text-zinc-200 uppercase tracking-wider block mb-2">Logo Brand Text</label>
                    <input
                      type="text"
                      value={settingsForm.logoText || ''}
                      onChange={(e) => setSettingsForm({ ...settingsForm, logoText: e.target.value })}
                      placeholder="RojrzTech"
                      style={inputStyle}
                      className="w-full px-4 py-3 text-xs font-bold"
                    />
                  </div>

                  <div>
                    <label className="text-xs font-black text-zinc-200 uppercase tracking-wider block mb-2">Logo Image URL (Optional Override)</label>
                    <input
                      type="text"
                      value={settingsForm.logoImageUrl || ''}
                      onChange={(e) => setSettingsForm({ ...settingsForm, logoImageUrl: e.target.value })}
                      placeholder="https://rojrztech.com/logo.png"
                      style={inputStyle}
                      className="w-full px-4 py-3 text-xs font-bold"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ================================================================= */}
          {/* 🎯 VIEW 10: META PIXEL & TRACKING SCRIPTS                         */}
          {/* ================================================================= */}
          {activeMenu === 'meta-pixel' && (
            <div className="space-y-6 max-w-4xl">
              <div className="p-6 sm:p-8 shadow-2xl space-y-6" style={cardStyle}>
                <div className="flex items-center justify-between pb-4 border-b-2 border-zinc-800">
                  <h3 className="text-base font-black text-[#F2B135] uppercase tracking-wider flex items-center gap-2">
                    <FiCode size={20} /> Meta Pixel & Analytics Manager
                  </h3>
                  <button
                    onClick={() => { handleSaveSettings('metaPixelId'); handleSaveSettings('googleAnalyticsId'); }}
                    className="btn-animated-gold !py-2 !px-4 text-xs"
                  >
                    <FiSave size={14} /> Save Tracking Codes
                  </button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-black text-zinc-200 uppercase tracking-wider block mb-2">Meta (Facebook) Pixel ID</label>
                    <input
                      type="text"
                      value={settingsForm.metaPixelId || ''}
                      onChange={(e) => setSettingsForm({ ...settingsForm, metaPixelId: e.target.value })}
                      placeholder="e.g. 123456789012345"
                      style={inputStyle}
                      className="w-full px-4 py-3 text-xs font-semibold"
                    />
                  </div>

                  <div>
                    <label className="text-xs font-black text-zinc-200 uppercase tracking-wider block mb-2">Google Analytics ID</label>
                    <input
                      type="text"
                      value={settingsForm.googleAnalyticsId || ''}
                      onChange={(e) => setSettingsForm({ ...settingsForm, googleAnalyticsId: e.target.value })}
                      placeholder="e.g. G-XXXXXXXXXX"
                      style={inputStyle}
                      className="w-full px-4 py-3 text-xs font-semibold"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ================================================================= */}
          {/* 📊 VIEW 11: OVERVIEW                                              */}
          {/* ================================================================= */}
          {activeMenu === 'overview' && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="p-6 rounded-3xl text-center shadow-xl" style={cardStyle}>
                <FiDatabase size={36} className="text-[#F2B135] mx-auto mb-3" />
                <h4 className="text-xs font-black text-zinc-200 uppercase tracking-wider">MySQL Database</h4>
                <p className="text-2xl font-black text-white mt-1">{dbStatus.database}</p>
                {dbStatus.loading ? (
                  <span className="text-xs text-zinc-400 font-mono font-bold mt-2 block">Checking...</span>
                ) : dbStatus.connected ? (
                  <span className="text-xs text-green-400 font-mono font-bold mt-2 block">✓ Connected @ {dbStatus.host}</span>
                ) : (
                  <span className="text-xs text-red-500 font-mono font-bold mt-2 block">❌ Offline / Connection Failed</span>
                )}
              </div>

              <div className="p-6 rounded-3xl text-center shadow-xl" style={cardStyle}>
                <FiFileText size={36} className="text-[#F2B135] mx-auto mb-3" />
                <h4 className="text-xs font-black text-zinc-200 uppercase tracking-wider">Total Published Posts</h4>
                <p className="text-4xl font-black text-white mt-1">{safeBlogs.length}</p>
                <span className="text-xs text-zinc-300 font-semibold mt-2 block">{publishedCount} Published • {draftCount} Drafts</span>
              </div>

              <div className="p-6 rounded-3xl text-center shadow-xl" style={cardStyle}>
                <FiCheckCircle size={36} className="text-[#F2B135] mx-auto mb-3" />
                <h4 className="text-xs font-black text-zinc-200 uppercase tracking-wider">Server Status</h4>
                <p className="text-2xl font-black text-green-400 mt-1">Express Server Active</p>
                <span className="text-xs text-zinc-300 font-semibold mt-2 block">Port 5000 REST APIs</span>
              </div>
            </div>
          )}

          {/* ================================================================= */}
          {/* 🖼️ VIEW 12: PORTFOLIO IMAGE EDITOR (LIVE BINDING)                 */}
          {/* ================================================================= */}
          {activeMenu === 'portfolio-images' && (
            <div className="space-y-6 max-w-5xl">
              <div className="p-6 sm:p-8 shadow-2xl space-y-6" style={cardStyle}>
                <div className="flex items-center justify-between pb-4 border-b-2 border-zinc-800">
                  <h3 className="text-base font-black text-[#F2B135] uppercase tracking-wider flex items-center gap-2">
                    <FiImage size={20} /> Portfolio Image Editor — Live Binding
                  </h3>
                  <button onClick={handleSavePortfolioItems} className="btn-animated-gold !py-2 !px-4 text-xs flex items-center gap-2">
                    <FiSave size={14} /> Save All & Update Live Site
                  </button>
                </div>

                <p className="text-xs text-zinc-300 bg-zinc-900 border border-zinc-700 p-3 rounded-xl">
                  ✅ <b className="text-[#F2B135]">Live Binding Active:</b> Saving updates will immediately reflect on the live portfolio section on your website homepage.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {portfolioItems.map((item, idx) => (
                    <div key={item.id} className="p-4 rounded-2xl space-y-3" style={{ backgroundColor: '#09090b', border: '2px solid #3f3f46' }}>
                      <div className="flex items-center gap-3">
                        <img
                          src={item.img}
                          alt={item.title}
                          className="w-16 h-16 rounded-xl object-cover border border-zinc-700"
                          onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80&w=200'; }}
                        />
                        <div className="flex-1">
                          <p className="text-xs font-black text-white mb-1">#{idx + 1} — {item.category}</p>
                          <p className="text-[10px] text-[#F2B135] font-bold uppercase tracking-wider">{item.title}</p>
                        </div>
                      </div>

                      <div>
                        <label className="text-[10px] font-black text-zinc-300 uppercase tracking-wider block mb-1">Title</label>
                        <input
                          type="text"
                          value={item.title}
                          onChange={(e) => {
                            const updated = [...portfolioItems];
                            updated[idx] = { ...updated[idx], title: e.target.value };
                            setPortfolioItems(updated);
                          }}
                          style={inputStyle}
                          className="w-full px-3 py-2 text-xs font-bold"
                        />
                      </div>

                      <div>
                        <label className="text-[10px] font-black text-zinc-300 uppercase tracking-wider block mb-1">Category</label>
                        <select
                          value={item.category}
                          onChange={(e) => {
                            const updated = [...portfolioItems];
                            updated[idx] = { ...updated[idx], category: e.target.value };
                            setPortfolioItems(updated);
                          }}
                          style={inputStyle}
                          className="w-full px-3 py-2 text-xs font-bold"
                        >
                          {['Website Design', 'Logo', 'Corporate Branding', 'E-Commerce', 'Video', 'Animation'].map(cat => (
                            <option key={cat} value={cat} style={{ backgroundColor: '#18181b', color: '#fff' }}>{cat}</option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="text-[10px] font-black text-zinc-300 uppercase tracking-wider block mb-1">Image URL</label>
                        <input
                          type="text"
                          value={item.img}
                          onChange={(e) => {
                            const updated = [...portfolioItems];
                            updated[idx] = { ...updated[idx], img: e.target.value };
                            setPortfolioItems(updated);
                          }}
                          style={inputStyle}
                          className="w-full px-3 py-2 text-xs font-mono"
                          placeholder="https://..."
                        />
                      </div>
                    </div>
                  ))}
                </div>

                <div className="flex items-center gap-4 pt-4 border-t-2 border-zinc-800">
                  <button
                    onClick={() => {
                      const newItem = {
                        id: portfolioItems.length + 1,
                        title: 'New Project',
                        category: 'Website Design',
                        img: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80&w=800'
                      };
                      setPortfolioItems([...portfolioItems, newItem]);
                    }}
                    className="px-5 py-3 rounded-xl bg-zinc-900 border-2 border-zinc-700 text-white hover:border-[#F2B135] hover:text-[#F2B135] text-xs font-black transition-all flex items-center gap-2"
                  >
                    <FiPlus size={14} /> Add New Portfolio Item
                  </button>
                  <button onClick={handleSavePortfolioItems} className="btn-animated-gold !py-3 !px-6 text-xs font-black flex items-center gap-2">
                    <FiSave size={16} /> Save & Update Live Site
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ================================================================= */}
          {/* ✉️ VIEW 13: HOSTINGER SMTP EMAIL SETTINGS                          */}
          {/* ================================================================= */}
          {activeMenu === 'smtp-settings' && (
            <div className="space-y-6 max-w-3xl">
              <div className="p-6 sm:p-8 shadow-2xl space-y-6" style={cardStyle}>
                <div className="flex items-center justify-between pb-4 border-b-2 border-zinc-800">
                  <h3 className="text-base font-black text-[#F2B135] uppercase tracking-wider flex items-center gap-2">
                    <FiMail size={20} /> Hostinger SMTP Email Settings
                  </h3>
                  <button onClick={handleSaveSmtp} className="btn-animated-gold !py-2 !px-4 text-xs flex items-center gap-2">
                    <FiSave size={14} /> Save SMTP Config
                  </button>
                </div>

                {/* Info banner */}
                <div className="p-4 rounded-2xl border-2 border-[#F2B135]/30 bg-[#F2B135]/10">
                  <p className="text-xs font-black text-[#F2B135] mb-2 flex items-center gap-2"><FiWifi size={14} /> Hostinger SMTP Configuration Guide</p>
                  <ul className="text-[11px] text-zinc-200 font-medium space-y-1 pl-4 list-disc">
                    <li><b>Host:</b> <code className="text-[#F2B135]">smtp.hostinger.com</code></li>
                    <li><b>Port (SSL):</b> <code className="text-[#F2B135]">465</code> &nbsp;|&nbsp; <b>Port (TLS):</b> <code className="text-[#F2B135]">587</code></li>
                    <li><b>Username:</b> Your full Hostinger email (e.g. <code className="text-[#F2B135]">info@rojrztech.com</code>)</li>
                    <li><b>Password:</b> Your Hostinger email account password</li>
                    <li>After saving, all contact form submissions will be emailed to your inbox!</li>
                  </ul>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-black text-zinc-200 uppercase tracking-wider block mb-2">SMTP Host</label>
                    <input
                      type="text"
                      value={smtpForm.smtpHost}
                      onChange={(e) => setSmtpForm({ ...smtpForm, smtpHost: e.target.value })}
                      placeholder="smtp.hostinger.com"
                      style={inputStyle}
                      className="w-full px-4 py-3 text-xs font-mono font-bold"
                    />
                  </div>

                  <div>
                    <label className="text-xs font-black text-zinc-200 uppercase tracking-wider block mb-2">SMTP Port (465 SSL / 587 TLS)</label>
                    <input
                      type="text"
                      value={smtpForm.smtpPort}
                      onChange={(e) => setSmtpForm({ ...smtpForm, smtpPort: e.target.value })}
                      placeholder="465"
                      style={inputStyle}
                      className="w-full px-4 py-3 text-xs font-mono font-bold"
                    />
                  </div>

                  <div>
                    <label className="text-xs font-black text-zinc-200 uppercase tracking-wider block mb-2">Email Username (Hostinger Email)</label>
                    <input
                      type="email"
                      value={smtpForm.smtpUser}
                      onChange={(e) => setSmtpForm({ ...smtpForm, smtpUser: e.target.value })}
                      placeholder="info@rojrztech.com"
                      style={inputStyle}
                      className="w-full px-4 py-3 text-xs font-bold"
                    />
                  </div>

                  <div>
                    <label className="text-xs font-black text-zinc-200 uppercase tracking-wider block mb-2">Email Password</label>
                    <input
                      type="password"
                      value={smtpForm.smtpPass}
                      onChange={(e) => setSmtpForm({ ...smtpForm, smtpPass: e.target.value })}
                      placeholder="••••••••••••"
                      style={inputStyle}
                      className="w-full px-4 py-3 text-xs font-bold"
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <label className="text-xs font-black text-zinc-200 uppercase tracking-wider block mb-2">Receiver Email (Where leads go)</label>
                    <input
                      type="email"
                      value={smtpForm.receiverEmail}
                      onChange={(e) => setSmtpForm({ ...smtpForm, receiverEmail: e.target.value })}
                      placeholder="info@rojrztech.com"
                      style={inputStyle}
                      className="w-full px-4 py-3 text-xs font-bold"
                    />
                  </div>
                </div>

                <button
                  onClick={handleSaveSmtp}
                  className="btn-animated-gold w-full py-4 text-xs font-black uppercase tracking-widest flex items-center justify-center gap-2 shadow-xl"
                >
                  <FiMail size={18} /> Save Hostinger SMTP Settings to Database
                </button>

                {smtpTestMsg && (
                  <div className="mt-3 p-3 rounded-xl bg-green-500/20 border border-green-500/40 text-green-300 text-xs font-bold">
                    {smtpTestMsg}
                  </div>
                )}
              </div>
            </div>
          )}

        </div>
      </main>

    </div>
  );
};

export default AdminDashboard;
